import { DeleteOutlined } from '@ant-design/icons';
import { App, AutoComplete, Button, Card, Input, InputNumber, Select, Switch, Tooltip } from 'antd';
import dayjs from 'dayjs';
import React, { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useMediaQuery } from 'react-responsive';
import {
  removeContractWorkPlaceItem,
  updateContractWorkPlaceItem,
} from '../../../../../contextRedux/Contract/ContractModel';
import { CustomTable } from '../../../../../customComponents/table/CustomTable';
import { RootState } from '../../../../../store';
import { getAvailablePeriods } from '../../../../../utils/contractUtils';
import './SummaryTab.css';

const { Option } = AutoComplete;

const SummaryTab: React.FC<any> = () => {
  const isMobile = useMediaQuery({ maxWidth: 720 });
  const { message } = App.useApp();

  const dispatch = useDispatch();
  const [, setSearchTerm] = useState<string>('');
  const typePeriod = ['lunar', 'trimestrial', 'semestrial', 'anual', 'la cerere'];
  const contract = useSelector((state: RootState) => state.models.contract);
  const company = useSelector((state: RootState) => state.models.contract.company);
  const workPlaces = useSelector((state: RootState) => state.models.contract.company?.workPlaces);
  const addedDevices = useSelector((state: RootState) => state?.models?.contract.contractWorkPlaceItems).map(
    (ad: any) => {
      const { piece, addition, unitmeasure, laborPriceOnHour, metadata } = ad;
      const devicePrice = Number(metadata?.deviceType?.price ?? 0);

      return {
        ...ad,
        price: Number(
          unitmeasure == 'hour'
            ? piece * laborPriceOnHour
            : (piece * devicePrice + (piece * devicePrice * addition) / 100).toFixed(2)
        ),
      };
    }
  );

  const availablePeriods = getAvailablePeriods(contract.dateStart, contract.dateEnd);
  const isItemExpired = (record: any) => {
    const itemDateEnd = record?.dateEnd ? dayjs(record.dateEnd) : dayjs(contract.dateEnd);
    return itemDateEnd.isValid() && itemDateEnd.endOf('day').isBefore(dayjs());
  };

  const handleRemoveEntry = useCallback(
    (_: any, index: number) => {
      dispatch(removeContractWorkPlaceItem(index));
    },
    [addedDevices]
  );

  const handleQuantityChange = useCallback(
    (record: any, value: number, _key?: string) => {
      if (!value) return;

      const updatedRecord = { ...record, piece: value, lastPiece: record.piece };
      const factor = updatedRecord.piece / updatedRecord.lastPiece;

      updatedRecord.price = Number((updatedRecord.price * factor).toFixed(2));
      updatedRecord.laborPrice = Number((updatedRecord.laborPrice * factor).toFixed(2));
      updatedRecord.laborTime = Number((updatedRecord.laborTime * factor).toFixed(2));

      if (updatedRecord.laborPrice < updatedRecord.device.laborPriceSM) {
        updatedRecord.laborPrice = updatedRecord.device.laborPriceSM;
      }

      if (updatedRecord.laborTime < updatedRecord.device.laborTimeSM) {
        updatedRecord.laborTime = updatedRecord.device.laborTimeSM;
      }

      dispatch(
        updateContractWorkPlaceItem({
          index: addedDevices.findIndex(
            (item: any) => item.deviceTypeId === record.deviceTypeId && item.workPlaceId === record.workPlaceId
          ),
          updates: updatedRecord,
        })
      );
    },
    [addedDevices, dispatch]
  );

  const handleDetailChange = useCallback(
    (record: any, value: string, _key: any) => {
      dispatch(
        updateContractWorkPlaceItem({
          index: addedDevices.findIndex(
            (item: any) => item.deviceTypeId === record.deviceTypeId && item.workPlaceId === record.workPlaceId
          ),
          updates: { ...record, details: value },
        })
      );
    },
    [addedDevices, dispatch]
  );

  const handleDateEndChange = useCallback(
    (record: any, value: string, _key: string) => {
      const isExpired = value ? dayjs(value).endOf('day').isBefore(dayjs()) : false;
      dispatch(
        updateContractWorkPlaceItem({
          index: addedDevices.findIndex(
            (item: any) => item.deviceTypeId === record.deviceTypeId && item.workPlaceId === record.workPlaceId
          ),
          updates: { ...record, dateEnd: value, isActive: isExpired ? false : (record.isActive ?? true) },
        })
      );
    },
    [addedDevices, dispatch]
  );

  const handleToggleStatus = useCallback(
    (record: any) => {
      const itemDateEnd = record?.dateEnd ? dayjs(record.dateEnd) : dayjs(contract.dateEnd);
      const expired = itemDateEnd.isValid() && itemDateEnd.endOf('day').isBefore(dayjs());
      const nextStatus = expired ? false : !(record.isActive ?? true);

      dispatch(
        updateContractWorkPlaceItem({
          index: addedDevices.findIndex(
            (item: any) => item.deviceTypeId === record.deviceTypeId && item.workPlaceId === record.workPlaceId
          ),
          updates: { ...record, isActive: nextStatus },
        })
      );
    },
    [addedDevices, contract.dateEnd, dispatch]
  );

  const handlePeriodChange = useCallback(
    (record: any, value: string) => {
      const updatedDevices = addedDevices.map((device: any) =>
        device.systemType.name === record.systemType.name && device.workPlaceId === record.workPlaceId
          ? {
              ...device,
              period: value,
              laborPrice:
                device.unitMeasure === 'hour'
                  ? device.piece * (device.laborPrice ?? device.laborPriceOnHour)
                  : device.device.laborPriceSM * device.piece,
            }
          : device
      );

      updatedDevices.forEach((updatedDevice: any) => {
        dispatch(
          updateContractWorkPlaceItem({
            index: addedDevices.findIndex(
              (item: any) =>
                item.deviceTypeId === updatedDevice.deviceTypeId && item.workPlaceId === updatedDevice.workPlaceId
            ),
            updates: updatedDevice,
          })
        );
      });
    },
    [addedDevices, dispatch]
  );

  const handleSelectAgent = (record: any, value: string) => {
    // Find the selected agent by its name
    const selectedAgent =
      company.contactPersons?.find((agent: any) => agent.name === value) ??
      workPlaces
        ?.filter((w: any) => w.id == record.workPlaceId)
        ?.flatMap((workPlace: any) => workPlace.contactPersons || [])
        ?.find((agent: any) => agent.name === value);

    if (!selectedAgent) {
      message.error('Agent nu a fost gasit!');
      return;
    }

    // Find the index of the record to update in addedDevices
    const recordIndex = addedDevices.findIndex(
      (device: any) => device.workPlaceId === record.workPlaceId && device.deviceTypeId === record.deviceTypeId
    );

    if (recordIndex === -1) {
      message.error('Inregistrarea nu a fost gasita!');
      return;
    }

    // Dispatch the update for the specific record
    dispatch(
      updateContractWorkPlaceItem({
        index: recordIndex,
        updates: {
          agent: selectedAgent,
          agentId: selectedAgent.id,
        },
      })
    );

    message.success(`Agentul a fost modificat cu ${selectedAgent.name}.`);
  };

  const handleSearchTermChange = (value: string) => {
    setSearchTerm(value);
  };

  const handleFilter = (inputValue: string, option: any) => {
    return option.value.toLowerCase().indexOf(inputValue.toLowerCase()) !== -1;
  };

  const columns = useMemo(
    () => [
      {
        title: 'No.',
        key: 'index',
        width: 100,
        render: (_text: string, _record: any, index: number) => index + 1 + '.',
      },
      {
        title: 'Device',
        key: 'device',
        render: (record: any) => (
          <div style={{ display: 'grid', gap: '8px' }}>
            <Tooltip title={record.device?.name}>
              <div className='ellipsis-cell'>
                <span>{record.device?.name}</span>
              </div>
            </Tooltip>
            {record.device?.imageLink?.[0]?.match(/\.(jpg|png)$/i) && (
              <img src={record.device.imageLink[0]} alt='Device' className='img-large-view' />
            )}
          </div>
        ),
      },
      {
        title: 'Locatie',
        dataIndex: ['workPlace', 'name'],
        key: 'workPlaceName',
      },
      {
        title: 'Persoana De Contact',
        dataIndex: ['agent', 'name'],
        key: 'agent',
        render: (text: string, record: any) => (
          <Select
            placeholder='Select an agent'
            onSelect={(e) => handleSelectAgent(record, e)}
            onChange={handleSearchTermChange}
            filterOption={handleFilter}
            value={text}
          >
            {[
              // If agent exists in equipmentTab, add it as the first option
              contract?.equipmentTab?.agent && !contract?.equipmentTab?.isOfferSelected && (
                <Option key={contract.equipmentTab.agent.id} value={contract.equipmentTab.agent.name}>
                  {contract.equipmentTab.agent.name}
                </Option>
              ),
              // Combine all contactPersons from company and workPlaces
              ...Array.from(
                new Map(
                  [
                    // Company contactPersons if offer is selected
                    ...(company?.contactPersons?.length > 0 && contract?.equipmentTab?.isOfferSelected
                      ? company.contactPersons.map((contactPerson: any) => ({
                          id: contactPerson?.id,
                          name: contactPerson?.name,
                          email: contactPerson?.email,
                          isActive: contactPerson?.isActive,
                          phone: contactPerson?.phone,
                          role: contactPerson?.role,
                        }))
                      : []),
                    // WorkPlaces' contactPersons
                    ...(workPlaces
                      ?.filter((w: any) => w?.id === record?.workPlaceId) // Match the current workPlaceId
                      ?.flatMap((workPlace: any) => workPlace?.contactPersons || []) // Flatten contactPersons
                      ?.map((contactPerson: any) => ({
                        id: contactPerson?.id,
                        name: contactPerson?.name,
                        email: contactPerson?.email,
                        isActive: contactPerson?.isActive,
                        phone: contactPerson?.phone,
                        role: contactPerson?.role,
                      })) || []),
                  ].map((contactPerson: any) => [contactPerson?.name, contactPerson]) // Use 'name' as the key
                ).values() // Extract unique values by name
              )
                ?.filter(Boolean) // Remove undefined/null entries
                ?.map((contactPerson: any, index: number) => (
                  <Option key={index} value={contactPerson.name}>
                    {contactPerson.name}
                  </Option>
                )),
            ]}
          </Select>
        ),
      },
      { title: 'Sistem', dataIndex: ['systemType', 'name'], key: 'system' },
      {
        title: 'Cantitate',
        dataIndex: 'piece',
        key: 'piece',
        render: (text: number, record: any, key: any) => (
          <InputNumber value={text} min={1} onChange={(value) => handleQuantityChange(record, value!, key)} />
        ),
      },
      {
        title: 'Pret manopera SM',
        dataIndex: ['device', 'laborPriceSM'],
        key: 'laborPriceSM',
        width: 200,
      },
      {
        title: 'Timp manopera SM',
        dataIndex: ['device', 'laborTimeSM'],
        key: 'laborTimeSM',
        width: 200,
      },
      {
        title: 'Data de incetare',
        dataIndex: 'dateEnd',
        key: 'dateEnd',
        render: (text: string, record: any, key: any) => {
          const formattedDate = text ? dayjs(text).format('YYYY-MM-DD') : dayjs(contract.dateEnd).format('YYYY-MM-DD');

          return (
            <Input
              type='date'
              value={formattedDate}
              min={contract.dateStart}
              onChange={(e) => handleDateEndChange(record, e.target.value, key)}
            />
          );
        },
      },
      {
        title: 'Periodicitate',
        dataIndex: 'period',
        key: 'period',
        render: (text: string, record: any) => (
          <Select value={text} onChange={(value) => handlePeriodChange(record, value)} style={{ width: '100%' }}>
            {addedDevices?.length <= 0
              ? availablePeriods.map((item: any, idx: any) => (
                  <Option key={idx} value={item}>
                    {' '}
                    {item}{' '}
                  </Option>
                ))
              : addedDevices?.length > 0 && addedDevices.some((ad: any) => ad.period == 'la cerere')
              ? ['la cerere']
                  ?.filter((period) => availablePeriods.includes(period))
                  .map((item, idx) => (
                    <Option key={idx} value={item}>
                      {item}
                    </Option>
                  ))
              : availablePeriods
                  ?.filter((t: any) => t != 'la cerere')
                  .map((item: any, idx: any) => (
                    <Option key={idx} value={item}>
                      {' '}
                      {item}{' '}
                    </Option>
                  ))}
          </Select>
        ),
      },
      {
        title: 'Status',
        key: 'status',
        render: (_: any, record: any) => {
          const isActive = !isItemExpired(record) && (record.isActive ?? true);
          const isExpired = isItemExpired(record);

          return (
            <Switch
              checked={isActive}
              disabled={isExpired}
              checkedChildren='Activ'
              unCheckedChildren='Inactiv'
              onChange={() => handleToggleStatus(record)}
            />
          );
        },
      },
      {
        title: 'Detalii',
        dataIndex: 'details',
        key: 'details',
        render: (text: string, record: any, key: any) => (
          <Input value={text} onChange={(e) => handleDetailChange(record, e.target.value, key)} />
        ),
      },
      {
        title: 'Action',
        dataIndex: 'action',
        key: 'action',
        fixed: 'right',
        render: (_: any, record: any, index: any) => (
          <Button
            type='link'
            onClick={() => handleRemoveEntry(record, index)}
            icon={<DeleteOutlined />}
            className='large-view-delete'
          >
            Şterge
          </Button>
        ),
      },
    ],
    [
      addedDevices,
      availablePeriods,
      contract.dateEnd,
      contract.dateStart,
      handleDateEndChange,
      handleDetailChange,
      handlePeriodChange,
      handleQuantityChange,
      handleRemoveEntry,
      handleToggleStatus,
      typePeriod,
    ]
  );

  const DeviceName = ({ name }: { name: string }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const maxLength = 50;
    return (
      <p className='deviceName'>
        <strong>{isExpanded || name.length <= maxLength ? name : `${name.slice(0, maxLength)}... `}</strong>
        {name.length > maxLength && (
          <a
            href='#'
            onClick={(e) => {
              e.preventDefault();
              setIsExpanded(!isExpanded);
            }}
            style={{ color: 'blue', cursor: 'pointer', marginLeft: '5px' }}
          >
            {isExpanded ? 'Read Less' : 'Read More'}
          </a>
        )}
      </p>
    );
  };

  return (
    <div className='deviced-added-container'>
      <h3>Devices added:</h3>

      {isMobile ? (
        <div style={{ display: 'grid', gap: '15px' }}>
          {addedDevices.map((device, index) => (
            <Card
              key={device.id}
              title={`Device ${index + 1}`}
              extra={
                <Button onClick={() => handleRemoveEntry(device, index)} icon={<DeleteOutlined />} className='delete' />
              }
              className='device-card'
            >
              {device.device?.imageLink?.[0]?.match(/\.(jpg|png)$/i) && (
                <img src={device.device.imageLink[0]} alt='Device' className='cardImage' />
              )}

              <p className='deviceName'>
                <strong>
                  <DeviceName name={device.device?.name} />
                </strong>
              </p>
              <p>
                <span className='label'>Locatie:</span> {device.workPlace?.name}
              </p>
              <p>
                <span className='label'>Persoana De Contact:</span>{' '}
                <Select
                  value={device.agent?.name}
                  onChange={(value) => handleSelectAgent(device, value)}
                  className='select'
                >
                  {company?.contactPersons?.map((contactPerson: any, index: number) => (
                    <Option key={index} value={contactPerson.name}>
                      {contactPerson.name}
                    </Option>
                  ))}
                </Select>
              </p>
              <p>
                <span className='label'>Sistem :</span> {device.systemType?.name}
              </p>
              <p>
                <span className='label'>Cantitate:</span>{' '}
                <InputNumber value={device.piece} min={1} onChange={(value) => handleQuantityChange(device, value)} />
              </p>
              <p>
                <span className='label'>Pret manopera SM: </span>
                {device.device?.laborPriceSM}
              </p>
              <p>
                <span className='label'>Timp manopera SM: </span>
                {device.device?.laborTimeSM}
              </p>
              <p>
                <span className='label'>Periodicitate :</span>{' '}
                <Select
                  value={device.period}
                  onChange={(value) => handlePeriodChange(device, value)}
                  className='select'
                >
                  {typePeriod.map((item, idx) => (
                    <Select.Option key={idx} value={item}>
                      {item}
                    </Select.Option>
                  ))}
                </Select>
              </p>
              <p>
                <span className='label'>Status :</span>{' '}
                <Switch
                  checked={!isItemExpired(device) && (device.isActive ?? true)}
                  disabled={isItemExpired(device)}
                  checkedChildren='Activ'
                  unCheckedChildren='Inactiv'
                  onChange={() => handleToggleStatus(device)}
                />
              </p>
              <p>
                <span className='label'>Detalii :</span>{' '}
                <Input value={device.details} onChange={(e) => handleDetailChange(device, e.target.value, index)} />
              </p>
            </Card>
          ))}
        </div>
      ) : (
        <CustomTable
          initialDataSource={addedDevices}
          rowKey={(addedDevice: (typeof addedDevices)[number]) => addedDevice.id}
          columns={columns}
          pagination={false}
        />
      )}
    </div>
  );
};

export default SummaryTab;
